<template>
    <PackSceneWrapper
        ref="refPackSceneWrapper"
        :active="active"
        :category="category"
        :color="packColor"
        :tier="tier"
        v-if="isHighTier"
    />
    <SlideToRip
        :enabled="packRip.isEnabled.value && isDesktopLayout"
        :progress="packRip.ripProgress.value"
        @dragstart="packRip.setSliderDragging(true)"
        @drag="packRip.setProgress"
        @dragend="packRip.setSliderDragging(false)"
    />

    <SwipeTutorial ref="refSwipeTutorial" dir="x" :active="showSwipeTutorial && isMobileLayout" />

    <slot />
</template>

<script>
    import { useRoute } from '#app';
    import { useBloom, useLayout } from '#imports';

    import {
        BurnTransition,
        DissolveTransition,
        FlyInTransition,
        FlyUpSpinTransition,
        RipTransition,
    } from './transitions';
    import SwipeTutorial from '~/components/common/SwipeTutorial.vue';
    import AsyncLabel from '~/components/gl/async-experience/AsyncLabel.vue';
    import ShineLayer from '~/components/gl/instant-rips/objects/GoldenTicket/GoldenShine';
    import { createLensflare } from '~/components/gl/Pack/elements/createLensflare';
    import { useManualPackRip } from '~/components/gl/Pack/transitions/useManualPackRip';
    import { useInteractionMotion } from '~/composables/useInteractionMotion';
    import { useModelExporter } from '~/composables/useModelExporter';
    import { PackBaseTier, PackTiers } from '~/core/constants';
    import { useColorPalette } from '~/providers/ColorPaletteProvider';
    import { useMatcap } from '~/providers/MatcapProvider';
    import { createLogger } from '~/utils/logger';

    const packLogger = createLogger('pack');

    const easeInSine = (t) => {
        return 1 - Math.cos((t * Math.PI) / 2);
    };

    const easeInPow = (t, power = 2) => {
        return Math.pow(t, power);
    };

    const easeOutPow = (t, power = 2) => {
        return 1 - Math.pow(1 - t, power);
    };

    export const TRANSITIONS_REVEAL = {
        flyIn: FlyInTransition,
        flyUpSpin: FlyUpSpinTransition,
    };

    export const TRANSITIONS_RIP = {
        rip: RipTransition,
        burn: BurnTransition,
        dissolve: DissolveTransition,
        ripAndBurn: RipTransition,
    };

    export const PackProviderKey = 'Pack';

    export const usePack = () => {
        const pack = inject(PackProviderKey, null);

        if (!pack) {
            packLogger.warn('Pack provider not found', {
                event: 'provider.missing',
                provider: 'Pack',
            });
            return {};
        }

        return pack;
    };
</script>

<script setup>
    import { useEventBus } from '@vueuse/core';
    import { gsap } from 'gsap';
    import {
        Group,
        InstancedMesh,
        LinearFilter,
        LinearMipmapLinearFilter,
        Object3D,
        Vector3,
    } from 'three';
    import {
        computed,
        inject,
        onBeforeUnmount,
        onMounted,
        provide,
        reactive,
        ref,
        shallowRef,
        watch,
        watchEffect,
    } from 'vue';

    import { LoaderEvent } from '@resn/gozer-loading';
    import { lerp, map, randomInt, toRadians } from '@resn/gozer-math';
    import { disposeMaterial } from '@resn/gozer-three/helpers';
    import {
        basePaneOptions,
        usePane,
        useRafBool,
        useThreeObject,
        useViewportResize,
    } from '@resn/gozer-vue';

    import PackMaterial from '../materials/PackMaterial';
    import { getAllMeshes } from '../utils';
    import { getTextures } from './data';
    import PackSceneWrapper from './scenes/PackSceneWrapper.vue';
    import SlideToRip from './SlideToRip.vue';
    import { useLoaderComplete } from '~/composables/useLoaderComplete';
    import { loadBitmapTexture } from '~/libs/three/loadBitmapTexture';
    import { useAudio } from '~/providers/AudioProvider';
    import { useGlobalAssets } from '~/providers/GlobalAssets';

    const PALETTE_DISSOLVE = ['#c5b3d2', '#b676cf', '#0a44df'];

    const props = defineProps({
        animations: { type: Array },

        image: { default: '' }, // super | mega | ultra | infinity
        tier: { type: Number, default: null },
        category: { type: String, default: null },
        variant: { type: String, default: null },
        type: { type: String, default: 'repack' },
        username: { type: String, default: null },
        cards: { type: Array, default: () => [] },
        productName: { type: String, default: null },

        color: { default: '' }, // #6044FF | #999999 | #ffd08b | #433566
        interactive: { type: Boolean, default: false },

        hasAmbientMotion: { type: Boolean, default: true },
        hasCompressedTextures: { type: Boolean, default: true },
        hasAutoLoad: { type: Boolean, default: true },
        swipeToOpen: { type: Boolean, default: false },
    });

    provide(PackProviderKey, {
        tier: computed(() => props.tier),
        category: computed(() => props.category),
        variant: computed(() => props.variant),
    });

    const refSwipeTutorial = ref(null);

    const active = ref(false);
    const showSwipeTutorial = ref(false);

    const isHighTier = computed(() => !!props.tier && !!props.category);
    const isGoldVariant = computed(() => props.variant === 'gold' && !isHighTier.value);
    const isSuperTier = computed(() => props.tier === 1);

    const { isDesktopLayout, isMobileLayout } = useLayout();

    const rgbImage = computed(() =>
        isHighTier.value ? PackTiers[props.tier]?.image : props.image
    );
    const packColor = computed(() => {
        if (isHighTier.value) {
            return isSuperTier.value && props.color ? props.color : PackTiers[props.tier]?.color;
        }
        return props.color || PackBaseTier.color;
    });
    const testColor = ref('#000000');

    const uRectSize = reactive({
        x: 0.378, //0.45,
        y: 0.651,
    });
    const uRectOffset = reactive({
        x: 0.21,
        y: 0.043,
    });

    const propsReactive = reactive({
        mcRotate: 0,
        mcScale: 2,
        mcRotateWorld: 0,

        colorDissolve: '#ff00ff',

        speed: 0.8,
        motionAmb: 0,
        bumpScale: 0.25,
    });

    const propsTransition = reactive({
        progress: 0,
        animating: false,

        revealId: null,
        ripId: null,
    });

    const propsAnimation = {
        position: new Vector3(),
        target: new Vector3(),

        rotation: new Vector3(),
        rotationTarget: new Vector3(),

        reduceMotion: true,
    };

    const refPackSceneWrapper = shallowRef(null);

    const basePackDefines = {
        USE_MATCAP_BASE: '',
        USE_MATCAP_0: '',
        USE_MATCAP_1: '',
        USE_MATCAP_IRRI: '',
    };

    const topShader = new PackMaterial({
        type: 'PackTopMaterial',
        defines: { IS_TOP: '', ...basePackDefines },
    });
    const bottomShader = new PackMaterial({
        type: 'PackBottomMaterial',
        defines: { IS_BOTTOM: '', ...basePackDefines },
    });
    const ripLineShader = new PackMaterial({
        type: 'PackRipLineMaterial',
        defines: { IS_RIP_LINE: '', ...basePackDefines },
    });

    const shaders = [topShader, bottomShader, ripLineShader];

    const vPosStart = new Vector3();
    const vPosAmbient = new Vector3();
    const vPosInteraction = new Vector3();
    const vRotSpin = new Vector3();

    const vRotStart = new Vector3();
    const vRotAmbient = new Vector3();

    const packObject = shallowRef(null);
    const ripLineObject = shallowRef(null);
    const packMeshes = shallowRef([]);

    const grpCustomMeshes = new Group();
    grpCustomMeshes.name = 'CustomMeshes';

    const { object } = useThreeObject(null, { name: 'Pack' });

    const innerObject = new Object3D();
    innerObject.name = 'Inner Object';
    object.add(innerObject);

    const interactionObject = new Object3D();
    interactionObject.name = 'Interaction Object';
    innerObject.add(interactionObject);

    const interaction = useInteractionMotion({ debugLabel: 'pack' });

    // Adds to the gltf pack object when loaded
    const sceneWrapperObject = new Object3D();
    sceneWrapperObject.name = 'Scene Wrapper Object';

    const bus = useEventBus('pack');
    const globalAssets = useGlobalAssets();
    const audio = useAudio();
    const route = useRoute();
    const viewport = useViewportResize();

    const bloom = useBloom();
    const { rotation: matcapRotation } = useMatcap();
    const { setColorPalette } = useColorPalette();
    const canShowCard = ref(false);
    const lensflare = createLensflare({
        pos: { x: 0, y: 0.54, z: 0.01 },
        dir: 'x',
        color: '#daecff',
        bgColor: '#7ab3ff',
    });
    innerObject.add(lensflare.object);

    const bgGlowColor = ref('#faf4ff');
    const bgGlow = new ShineLayer(object);
    bgGlow.scale.setScalar(6);
    bgGlow.material.depthTest = true;
    bgGlow.material.uniforms.lightColor.value.set(bgGlowColor.value);
    bgGlow.renderOrder = -2;
    bgGlow.hide();

    const packRip = useManualPackRip({
        maxDistance: computed(() => viewport.height * 0.3),
        onRipping: (val) => onManualRipping(val),
        // onFullyRipping: (val) => {
        //     if (val) refSwipeTutorial.value.startInteraction();
        //     else refSwipeTutorial.value.restartInteraction();
        // },
        onUpdate: (progress) => {
            // const pr = lerp(progress, 0, 0.25);
            propsTransition.progress = lerp(progress, 0, 0.25);
            setRipProgress();
        },
        onComplete: () => {
            lensflare.complete();
            showSwipeTutorial.value = false;
            open();
        },
    });

    watch(
        [() => packRip.isEnabled.value, () => packRip.isRipping.value],
        ([isEnabled, isRipping]) => {
            if (!isEnabled) {
                lensflare.stopInterval();
                return;
            }

            if (isRipping) {
                lensflare.setMode('ripping');
            } else {
                lensflare.setMode('idle');
            }
        },
        { immediate: true }
    );

    // const isExport = computed(() => route.fullPath.includes('export'));

    let transitionReveal, transitionRip;

    onMounted(() => {
        setRGBImage(rgbImage.value);
        setCategory(props.category);
        setScene(refPackSceneWrapper.value);
        setColor(packColor.value);

        window.packMaterial = bottomShader;

        loader.start();
    });

    const init = () => {
        const pack = packObject.value;

        if (!pack) return;

        packMeshes.value = getAllMeshes(pack);

        pack.add(grpCustomMeshes);
        pack.add(lensflare.object);

        disposeMaterials();

        vPosStart.copy(pack.position);
        vRotStart.copy(pack.rotation);

        interactionObject.add(pack);
        pack.add(sceneWrapperObject);

        // Add rip line
        const topObject = pack.getObjectByName('pack_top');
        ripLineObject.value = pack.getObjectByName('pack_foil_line');
        if (ripLineObject.value) {
            if (bloom.pass?.value) bloom.pass.value.selection?.add(ripLineObject.value);
        }

        if (globalAssets.assets) setupObject();
        else globalAssets.loader.once(LoaderEvent.LOAD_COMPLETE, setupObject);

        // const ripId =
        //     Object.keys(TRANSITIONS_RIP)[
        //         randomInt(0, Object.keys(TRANSITIONS_RIP).length - 1)
        //     ];
        // const ripId = 'rip';
        // setTransitionRip(ripId);
    };

    const setupObject = () => {
        updateMaterial();
        updateTextures();

        setTier(props.tier);
    };

    const disposeMaterials = () => {
        packMeshes.value.forEach((node) => disposeMaterial(node.material));

        // Dispose godrays if they exist
        const godrays = object.parent?.children?.find((child) => child instanceof InstancedMesh);
        if (godrays) {
            godrays.geometry.dispose();
            godrays.material.dispose();
            object.parent.remove(godrays);
        }
    };

    let readyCount = 0;
    let readyTriggered = false;
    const checkReady = () => {
        if (readyCount === 2 && !readyTriggered) {
            bus.emit('ready'); // Emit "ready" event once both textures and RGB are loaded
            bus.reset();
            readyTriggered = true;
        }
    };

    const setTransitionReveal = (id) => {
        if (!TRANSITIONS_REVEAL[id]) {
            packLogger.warn('Pack reveal transition not found', {
                event: 'pack.transition_missing',
                transitionType: 'reveal',
                transitionId: id,
            });
            return;
        }

        if (transitionReveal) {
            shaders.forEach((shader) => shader.clearTransitionUniforms({ reveal: true }));

            transitionReveal.destroy();
            transitionReveal = null;
        }
        const TransitionClass = TRANSITIONS_REVEAL[id] || FlyInTransition;
        transitionReveal = new TransitionClass(packObject.value, bottomShader, propsAnimation);
        applyTransition(transitionReveal);

        propsTransition.revealId = id;
    };

    const setTransitionRip = (id) => {
        if (!TRANSITIONS_RIP[id]) {
            packLogger.warn('Pack rip transition not found', {
                event: 'pack.transition_missing',
                transitionType: 'rip',
                transitionId: id,
            });
            return;
        }

        if (transitionRip) {
            shaders.forEach((shader) => shader.clearTransitionUniforms({ rip: true }));

            transitionRip.destroy();
            transitionRip = null;
        }
        const TransitionClass = TRANSITIONS_RIP[id] || RipTransition;
        transitionRip = new TransitionClass(
            packObject.value,
            id === 'ripAndBurn'
                ? { mode: RipTransition.MODES.BURN }
                : { mode: RipTransition.MODES.DEFAULT }
        );
        applyTransition(transitionRip);

        propsTransition.ripId = id;
    };

    const updateMaterial = () => {
        packMeshes.value.forEach((node) => {
            node.renderOrder = 2;

            switch (node.name) {
                case 'pack_foil_line':
                    node.material = ripLineShader;
                    break;
                case 'pack_top':
                    node.material = topShader;
                    break;
                case 'pack_bottom':
                    node.material = bottomShader;
                    break;
            }
        });
    };

    const applyTransition = (transition) => {
        if (!transition) return;

        if (transition.createCustomMeshes) transition.createCustomMeshes(grpCustomMeshes);
        if (transition.setTransitionUniforms)
            shaders.forEach((shader) => transition.setTransitionUniforms(shader));

        shaders.forEach((shader) => (shader.transparent = transition.transparent));
    };

    const updateTextures = () => {
        const {
            matcapPackGlobal,
            matcapPackGold,
            matcapPackWhite,
            matcapPackBlack,
            matcapIrridescent3,
            pNoise,
            pNoiseNormal,
            pNoiseNormalGold,
        } = globalAssets.assets;

        shaders.forEach((shader) => {
            shader.tMatcap = isGoldVariant.value ? matcapPackGold : matcapPackGlobal; // ✅
            shader.tMatcap0 = matcapPackWhite; // ✅
            shader.tMatcap1 = matcapPackBlack; // ✅
            shader.tMatcapIrri = matcapIrridescent3; // ✅
            shader.tNoiseDissolve = pNoise;

            shader.normalMap = pNoiseNormalGold; //isGoldVariant.value ? pNoiseNormalGold : pNoiseNormal;
        });

        readyCount++;
        checkReady();
    };

    const setVariant = (val) => {
        // Guard against assets not being loaded yet (e.g., when joining midway)
        if (!globalAssets.assets) return;

        const { matcapPackGold, matcapPackGlobal, pNoiseNormalGold, pNoiseNormal } =
            globalAssets.assets;
        shaders.forEach((shader) => {
            shader.tMatcap = isGoldVariant.value ? matcapPackGold : matcapPackGlobal;
            shader.normalMap = pNoiseNormalGold; //isGoldVariant.value ? pNoiseNormalGold : pNoiseNormal;
            shader.defines.USE_COLOR = !isGoldVariant.value;
            shader.needsUpdate = true;
        });
    };

    const setUniforms = (props) => {
        const { colorDissolve } = props;
        shaders.forEach((shader) => {
            shader.u_color_dissolve.set(colorDissolve);
            shader.u_transition_progress = propsTransition.progress;

            shader.u_matcapRotationWorld = props.mcRotateWorld;
            shader.u_matcapScale = props.mcScale;

            shader.bumpScale = props.bumpScale;
            shader.normalScale.setScalar(props.bumpScale);

            // shader.needsUpdate = true;
        });
    };

    const setRGBImage = (url) => {
        if (!url) {
            packLogger.warnOnce('Pack RGB texture URL missing', {
                event: 'asset.texture_missing',
                onceKey: 'asset.texture_missing:Pack:rgb',
                textureKind: 'rgb',
                component: 'Pack',
            });
            return;
        }
        loadBitmapTexture(url, { flipY: true }).then((texture) => {
            shaders.forEach((shader) => {
                shader.tRGB?.dispose();
                shader.tRGB = texture;
            });

            readyCount++;
            checkReady();
        });
    };

    const setTier = (tier) => {
        const tierData = PackTiers[tier];

        // packColor .value = (tierData && isHighTier.value) ? tierData.color : PackBaseTier.color;
    };

    const setColor = (val) => {
        bgGlow.material.uniforms.lightColor.value.set(val);

        shaders.forEach((shader) => {
            shader.u_color.set(val);
            // shader.defines.USE_COLOR = shader.u_color.getHex() !== 0x000000;
        });
    };

    const setCategory = (category) => {
        if (!category) {
            return;
        }
    };

    const setActive = (val) => {
        innerObject.visible = val;
    };

    const onManualRipping = (val) => {
        const motionAmb = val ? 0 : 1;
        const duration = val ? 0.5 : 0.5;

        gsap.killTweensOf(vPosInteraction);
        gsap.to(vPosInteraction, { z: val ? -0.2 : 0, duration, ease: 'power3.out' });

        if (!packRip.reachedThreshold.value) {
            gsap.killTweensOf(propsReactive, 'motionAmb');
            gsap.to(propsReactive, { motionAmb, duration, ease: 'power2.out' });
        }

        if (val) {
            bgGlow.show({ intensity: 0.5, duration: 1 });
        } else if (!packRip.reachedThreshold.value) {
            bgGlow.hide();
        }
    };

    const setShowSwipeTutorial = (val) => {
        gsap.killTweensOf(vPosInteraction, 'y');
        gsap.to(vPosInteraction, {
            y: val ? -0.35 : 0,
            duration: val ? 1 : 0.8,
            ease: val ? 'power2.inOut' : 'power3.out',
        });
    };

    const setScene = (scene) => {
        const useScene = !!scene;

        bottomShader.USE_SCENE = useScene;
        bottomShader.tScene = useScene ? scene.fbo.texture : null;
        // bottomShader.needsUpdate = true;

        if (scene) {
            const { object: obj } = scene;
            sceneWrapperObject.add(obj);
        }
    };

    const setRipProgress = () => {
        const { progress } = propsTransition;

        canShowCard.value = progress >= 0.2;

        const flarePr = map(progress, 0.03, 0.3, 0, 1, true);
        lensflare.setProgress(flarePr);

        if (transitionRip?.setProgress) transitionRip.setProgress(progress);

        shaders.forEach((shader) => (shader.u_transition_progress = progress));
    };

    const update = () => {
        propsAnimation.position.copy(propsAnimation.target);
        propsAnimation.rotation.copy(propsAnimation.rotationTarget);

        const ts = performance.now() / 1000;
        const { speed, motionAmb } = propsReactive;

        // Calculate ambient position (gentle floating)
        vPosAmbient.set(0, Math.sin(ts * speed * 0.2), 0).multiplyScalar(0.04 * motionAmb);

        // Calculate ambient rotation (gentle swaying)
        vRotAmbient
            .set(
                Math.sin(ts * speed * 0.4) * 0.2,
                Math.sin(ts * speed * 0.6) * 0.4,
                Math.cos(ts * speed * 0.3) * -0.02
            )
            .multiplyScalar(0.5 * motionAmb);

        interaction.update();

        object.position
            .copy(vPosStart)
            .add(propsAnimation.position)
            .add(vPosAmbient)
            .add(vPosInteraction);
        object.rotation.setFromVector3(
            vRotStart.clone().add(vRotSpin).add(propsAnimation.rotation).add(vRotAmbient)
        );

        interactionObject.position.copy(interaction.motion.pos).add(interaction.pressMotion);
        interactionObject.quaternion.copy(interaction.motion.final);

        // refPackSceneWrapper.value.object.rotation.setFromVector3(
        //     vRotStart.clone().multiplyScalar(-1)
        // );

        // ― transition
        // dont understand this logic, commenting out for now
        // if (propsTransition.animating) {
        //     const twoPi = 2 * Math.PI;
        //     const t = Math.round(bottomShader.u_matcapRotation / twoPi) * twoPi;
        //     shaders.forEach(
        //         (shader) => (shader.u_matcapRotation += (t - shader.u_matcapRotation) * 0.03)
        //     );
        // } else {}

        shaders.forEach((shader) => {
            shader.u_matcapRotation = matcapRotation.value;
            shader.u_time = performance.now() / 1000;
        });

        if (transitionRip) transitionRip.update?.(propsTransition.progress);
        if (transitionReveal) transitionReveal.update?.(propsTransition.progress);

        bgGlow.animate({ time: ts });
    };

    useRafBool(active, update);

    let tlShow, tlOpen;

    const { CustomEase } = inject('gsap', { CustomEase: null });
    const customEase = 'M0,0 C0.299,0 0.284,0.794 0.501,1.004 0.66,1.157 0.749,0.971 1,1 ';
    // const customEase = 'M0,0 C0.3,0.154 0.296,0.864 0.5,1 0.679,1.118 0.858,1 1,1 ';

    const ease = CustomEase ? CustomEase.create('custom', customEase) : 'back.inOut(1.2)';

    const show = ({
        delay = 0,
        transitionId = propsTransition.revealId,
        spinDir = 1,
        spinDelay = 0.8,
        animate = true,
        onComplete = () => {},
    } = {}) => {
        const motionAmb = Math.max(+props.hasAmbientMotion, 0.001);

        if (transitionId) setTransitionReveal(transitionId);

        const ripId = 'rip';
        setTransitionRip(ripId);

        tlShow?.kill();
        tlShow = gsap
            .timeline({
                delay,
                onStart: () => {
                    update();
                    active.value = true;

                    const tierMeta = PackTiers[props.tier];
                    if (tierMeta) {
                        setColorPalette(null, { paletteId: tierMeta.paletteId });
                    }
                },
                onComplete: () => {
                    setTransitionRip('rip');
                    if (props.swipeToOpen) packRip.enable();
                    onComplete();
                },
            })
            .add(transitionReveal?.start(), 0)
            .to(propsReactive, { motionAmb, duration: 2, ease: 'sine.out' }, 0);

        if (animate) {
            tlShow.fromTo(
                vRotSpin,
                { y: 0 },
                { y: toRadians(360 * spinDir), duration: 2, ease: ease },
                spinDelay
            );
        }

        return tlShow;
    };

    const open = ({ delay = 0, transitionId = propsTransition.ripId } = {}) => {
        propsTransition.animating = true;
        // propsTransition.progress = 0;

        vRotSpin.y = 0;

        propsReactive.colorDissolve = PALETTE_DISSOLVE[randomInt(0, PALETTE_DISSOLVE.length - 1)];

        // setTransitionRip(
        //     transitionId ||
        //         Object.keys(TRANSITIONS_RIP)[randomInt(0, Object.keys(TRANSITIONS_RIP).length - 1)]
        // );

        const duration = (transitionRip?.duration || 3) * (1 - propsTransition.progress);

        tlOpen?.kill();
        tlOpen = gsap
            .timeline({
                delay,
                onStart: () => {
                    active.value = true;
                    emit('openStart');
                },
                onComplete: () => {
                    active.value = false;

                    emit('openComplete');
                },
            })
            .to(propsReactive, { motionAmb: 0, duration: 1, ease: 'sine.out' }, 0)
            .to(
                propsTransition,
                {
                    progress: 1,
                    duration,
                    ease: 'linear.none',
                    onUpdate: setRipProgress,
                },
                0
            )
            .add(() => lensflare.complete(), 0.3)
            .add(() => bgGlow.hide({ duration: 0.7 }), 0);
        audio.play('packDisolve');

        return tlOpen;
    };

    const hide = ({ delay = 0 } = {}) => {
        if (delay) {
            tlShow?.kill();
            tlShow = gsap.delayedCall(delay, hideComplete);
        } else {
            hideComplete();
        }
    };

    const hideComplete = () => {
        // innerObject.visible = false;
        // active.value = false;

        // gsap.set(propsTransition, { progress: 1, motionAmb: 0 });
        reset();
    };

    const resetTransitions = () => {
        ['position', 'rotation', 'target', 'rotationTarget'].forEach((key) =>
            propsAnimation[key]?.setScalar(0)
        );

        if (transitionReveal) {
            transitionReveal.destroy();
            transitionReveal = null;
        }
        if (transitionRip) {
            transitionRip.destroy();
            transitionRip = null;
        }

        shaders.forEach((shader) => shader.clearTransitionUniforms({ reveal: true, rip: true }));
    };

    const reset = (overrides = {}) => {
        active.value = false;

        propsReactive.motionAmb = 0;

        propsTransition.progress = 0;
        propsTransition.animating = false;

        [vRotAmbient, vPosAmbient, vRotSpin].forEach((vec) => vec.setScalar(0));

        tlShow?.kill();
        tlOpen?.kill();

        resetTransitions();

        Object.assign(propsReactive, overrides);
    };

    const { hasCompressedTextures } = props;

    const onLoadComplete = ({ data }) => {
        const noFlipTextures = ['mapBumpPack', 'mapEdgesPack'];

        for (const key in data) {
            const map = data[key];
            map.name = key;
            // map.colorSpace = LinearSRGBColorSpace;
            map.flipY = !noFlipTextures.includes(key);

            // Special handling for bump map with compressed textures
            if (key === 'mapBumpPack' && hasCompressedTextures) {
                map.generateMipmaps = true;
                map.minFilter = LinearMipmapLinearFilter;
                map.magFilter = LinearFilter;
            }

            // map.needsUpdate = true;
        }

        const { gltf } = data;
        const pack = gltf.scene;

        packObject.value = pack;

        init();

        emit('loaded');
    };

    const loader = useLoaderComplete(
        {
            ...getTextures({ compressed: hasCompressedTextures }),
            gltf: '/glb/pack-etc1s@lg.glb',
        },
        onLoadComplete
    );

    const setInteractive = (val) => {
        if (val) interaction.enable();
        else interaction.disable();
    };

    onBeforeUnmount(() => {
        disposeMaterials(packObject.value);
    });

    watch(active, setActive, { immediate: true });

    watch(interaction.pinchingOrGrabbing, (val) => {
        if (!props.interactive) return;

        const motionAmb = val ? 0 : Math.max(+props.hasAmbientMotion, 0.001);
        const duration = val ? 0.5 : 4;
        const ease = val ? 'power2.out' : 'sine.in';

        gsap.killTweensOf(propsReactive, 'motionAmb');
        gsap.to(propsReactive, { motionAmb, duration, ease });
    });

    watch(() => props.tier, setTier);
    watch(() => props.category, setCategory);
    watch(() => props.variant, setVariant);
    watch(() => props.interactive, setInteractive);
    // watch(() => refSwipeTutorial.value?.active, setShowSwipeTutorial);

    watch(propsReactive, setUniforms, { immediate: true });

    watch(rgbImage, setRGBImage);
    watch(testColor, setColor);
    watch(packColor, setColor);
    watch(refPackSceneWrapper, setScene);
    watch(bgGlowColor, (val) => {
        bgGlow.material.uniforms.lightColor.value.set(val);
    });

    const enableRip = () => {
        packRip.enable();
        showSwipeTutorial.value = true;
    };

    // Expose component API
    defineExpose({
        show,
        hide,
        open,
        reset,
        object,
        setRipProgress,
        setTransitionRip,
        canShowCard,
        enableRip,
        shader: bottomShader,
        exposedVectors: { vRotStart, vPosStart },
    });

    const emit = defineEmits(['loaded', 'openStart', 'openComplete']);

    const uPeelRadius = ref(0.22);
    const uPeelPosX = ref(0.2);
    const uPeelPosY = ref(0);
    const uPeelRotZ = ref(1.3);

    watchEffect(() => {
        const peelRadius = uPeelRadius.value;
        const peelPosX = uPeelPosX.value;
        const peelPosY = uPeelPosY.value;
        const peelRotZ = uPeelRotZ.value;

        shaders.forEach((shader) => {
            shader.uPeelRadius = peelRadius;
            shader.uPeelPosX = peelPosX;
            shader.uPeelPosY = peelPosY;
            shader.uPeelRotZ = peelRotZ;
        });
    });

    watchEffect(() => {
        bottomShader.uRectSize.set(uRectSize.x, uRectSize.y);
        bottomShader.uRectOffset.set(uRectOffset.x, uRectOffset.y);
    });

    const modelExport = useModelExporter();

    const pos = reactive({ x: 0, y: 0, z: 0 });
    const rot = reactive({ x: 0, y: 0, z: 0 });
    watchEffect(() => {
        innerObject.position.set(pos.x, pos.y, pos.z);
        innerObject.rotation.set(rot.x, rot.y, rot.z);
    });

    // Setup debug panel if enabled
    if (!modelExport) {
        const pane = usePane(
            [
                { name: 'pos', value: pos },
                { name: 'rot', value: rot },
                // {
                //     value: propsReactive,
                //     options: {
                //         onChange: (e) => {
                //             const { key, value } = e.target;
                //             if (key === 'mcRotate') bottomShader.u_matcapRotation = value;
                //             if (key === 'mcScale') bottomShader.u_matcapScale = value;
                //         },
                //     },
                // },
                { name: 'color', value: testColor },
                { name: 'bgGlowColor', value: bgGlowColor },
                { name: 'uRectSize', value: uRectSize, options: basePaneOptions },
                { name: 'uRectOffset', value: uRectOffset, options: basePaneOptions },
                // Rip transition uniforms
                // {
                //     name: 'uPeelRadius',
                //     value: uPeelRadius,
                //     options: { min: 0, max: 5, step: 0.01 },
                // },
                // { name: 'uPeelPosX', value: uPeelPosX, options: { min: -4, max: 4, step: 0.01 } },
                // { name: 'uPeelPosY', value: uPeelPosY, options: { min: -4, max: 4, step: 0.01 } },
                // {
                //     name: 'uPeelRotZ',
                //     value: uPeelRotZ,
                //     options: { min: 0, max: 3.14, step: 0.001 },
                // },
            ],
            {
                title: 'Pack',
                expanded: true,
            }
        );

        if (pane) {
            pane.addBlade({
                view: 'list',
                label: 'Reveal',
                options: Object.keys(TRANSITIONS_REVEAL).map((key) => ({ text: key, value: key })),
                value: propsTransition.revealId,
            }).on('change', (e) => setTransitionReveal(e.value));

            pane.addBlade({
                view: 'list',
                label: 'Rip',
                options: Object.keys(TRANSITIONS_RIP).map((key) => ({ text: key, value: key })),
                value: propsTransition.ripId,
            }).on('change', (e) => setTransitionRip(e.value));

            pane.addBinding(propsTransition, 'progress', { min: 0, max: 1 }).on('change', (e) => {
                propsTransition.progress = e.value;
                setRipProgress();
            });
        }
    }
</script>

<style lang="scss" scoped>
    @use '@/styles/shared.scss' as *;

    .pack__swipeLabel {
        position: absolute;
        top: 10%;
        left: 50%;
        transform: translateX(-50%);
    }
</style>
